import { useCallback, useEffect, useState } from 'react';

import { selectAccessibilityPrefs } from '../../../db/app/selectors';
import { FileAttachment } from '../../../db/shared/types';
import { useAppSelector } from '../../../store/hooks';
import { uploadFormula as _uploadFormula, uploadImage as _uploadImage } from '../helpers';

export interface VMProps {
  initialAttachments?: FileAttachment[];
  onAttachmentsChange?: (attachments: FileAttachment[]) => any;
}

const INITIAL_ATTACHMENTS: FileAttachment[] = [];

export default function useRichTextEditorVM({
  initialAttachments = INITIAL_ATTACHMENTS,
  onAttachmentsChange,
}: VMProps) {
  const { turnOff } = useAppSelector((state) => selectAccessibilityPrefs(state));
  const isScreenReaderOn = !turnOff;

  const [uploadingImageCount, setUploadingImageCount] = useState(0);
  const [attachments, setAttachments] = useState(initialAttachments);

  const uploadFormula = useCallback(_uploadFormula, []);

  const uploadImage = useCallback(async (file: File) => {
    try {
      setUploadingImageCount((count) => count + 1);
      const result = await _uploadImage(file);
      return result;
    } finally {
      setUploadingImageCount((count) => count - 1);
    }
  }, []);

  const handleAttachmentUpload = (attachment: FileAttachment) => {
    setAttachments((attachments) => [...attachments, attachment]);
  };

  const handleAttachmentDelete = (attachment: FileAttachment) => () => {
    setAttachments((attachments) =>
      attachments.filter(
        (a) =>
          `${a.name}-${a.extension}-${a.originalName}` !==
          `${attachment.name}-${attachment.extension}-${attachment.originalName}`
      )
    );
  };

  useEffect(
    function resetAttachments() {
      setAttachments(initialAttachments);
    },
    [initialAttachments]
  );

  useEffect(
    function notifyAttachmentChange() {
      onAttachmentsChange?.(attachments);
    },
    // FIXME: attachments changes very often due to an unknown reason which triggers this useEffect infinite times
    // onAttachmentsChange also changes very often, may be due to a change in state.
    // so both of those props are not added as a direct dependency of this useEffect
    [JSON.stringify(attachments)]
  );

  return {
    isScreenReaderOn,
    attachments,
    isUploadingImage: uploadingImageCount > 0,
    onAttachmentUpload: handleAttachmentUpload,
    onAttachmentDelete: handleAttachmentDelete,
    uploadFormula,
    uploadImage,
  };
}
